import {
  EditOutlined as EditOutlinedIcon,
  RemoveRedEyeOutlined as RemoveRedEyeOutlinedIcon,
} from '@mui/icons-material';
import { Box, IconButton, Table, TableBody, TableRow } from '@mui/material';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress/Progress';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import { IProductCategoriesWithProducts } from 'src/interfaces';
import { useUserRole } from 'src/utils';

import { useManageProductCategoryPage } from '../../hooks/ManageProductCategoryPage.hook';
import {
  ArrowIcon,
  CustomTableCell,
  CustomTableCellRight,
  CustomTableHead,
  CustomTableRow,
  InViewBoxContainer,
  KeyboardArrowDownIconStyled,
  KeyboardArrowUpIconStyled,
  TableBodyContainer,
} from '../../ManageProductCategory.styled';
import { SORT_DIRECTION } from '../../ManageProductCategoryPage.interface';

export const ManageProductCategoryGridTable = () => {
  const { ref: inViewRef, inView } = useInView({ threshold: 0.1 });
  const navigate = useNavigate();
  const { isViewOnly } = useUserRole();
  const {
    isLoading,
    getNextProductCategoriesWithProductsPage,
    columns,
    handleSort,
    activeColumn,
    productCategoriesWithProducts,
    sorting,
    hasNextPage,
  } = useManageProductCategoryPage();

  useEffect(() => {
    if (inView) {
      getNextProductCategoriesWithProductsPage();
    }
  }, [getNextProductCategoriesWithProductsPage, inView]);

  if (isLoading) {
    return <Progress />;
  }
  const hasData =
    productCategoriesWithProducts &&
    productCategoriesWithProducts?.pages.flatMap((page) => page.data).length >
      0;

  const productCategoryTableData = (
    row: IProductCategoriesWithProducts,
    column: (typeof columns)[number],
  ) => {
    const columnValues =
      row[column.key as keyof IProductCategoriesWithProducts];

    if (typeof columnValues === 'string' && columnValues.length > 30) {
      return (
        <CustomTooltip
          width={500}
          placement="bottom"
          title={columnValues}
          overflowWidth={250}>
          <span>{columnValues}</span>
        </CustomTooltip>
      );
    }

    return <span>{columnValues}</span>;
  };

  return (
    <>
      {hasData ? (
        <TableBodyContainer>
          <Table stickyHeader>
            <CustomTableHead>
              <TableRow>
                {columns.map((column) => (
                  <CustomTableCell key={column.key} isdoublewidth={column.key}>
                    <Box
                      display="flex"
                      border="none"
                      alignItems="center"
                      justifyContent="start"
                      gap={3}>
                      {column.label}
                      {column.sortIcon && (
                        <Box
                          width={14}
                          height={31}
                          display="flex"
                          flexDirection="column"
                          justifyContent="space-around"
                          marginRight={0}>
                          <ArrowIcon
                            onClick={() =>
                              handleSort(column.key, SORT_DIRECTION.ASC)
                            }>
                            <KeyboardArrowUpIconStyled
                              active={
                                activeColumn === column.key &&
                                sorting.order === SORT_DIRECTION.ASC
                              }
                            />
                          </ArrowIcon>
                          <ArrowIcon
                            onClick={() =>
                              handleSort(column.key, SORT_DIRECTION.DESC)
                            }>
                            <KeyboardArrowDownIconStyled
                              active={
                                activeColumn === column.key &&
                                sorting.order === SORT_DIRECTION.DESC
                              }
                            />
                          </ArrowIcon>
                        </Box>
                      )}
                    </Box>
                  </CustomTableCell>
                ))}
                <CustomTableCell />
                <CustomTableCell />
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {!isLoading &&
                productCategoriesWithProducts?.pages.map((page) =>
                  page.data.map((row) => (
                    <CustomTableRow key={row.id}>
                      {columns.map((column) => (
                        <CustomTableCell
                          key={column.key}
                          isdoublewidth={column.key}>
                          {productCategoryTableData(row, column)}
                        </CustomTableCell>
                      ))}
                      <CustomTableCellRight>
                        <IconButton
                          size="small"
                          aria-label="more"
                          onClick={() => {
                            navigate(
                              `${PATHS.PRODUCTS}?product_category_id=${row.id}`,
                            );
                          }}>
                          <RemoveRedEyeOutlinedIcon />
                        </IconButton>
                      </CustomTableCellRight>

                      <CustomTableCellRight>
                        {!isViewOnly && (
                          <IconButton
                            size="small"
                            aria-label="more"
                            onClick={() => {
                              navigate(
                                PATHS.PRODUCT_CATEGORIES_EDIT(`${row.id}`),
                              );
                            }}>
                            <EditOutlinedIcon />
                          </IconButton>
                        )}
                      </CustomTableCellRight>
                    </CustomTableRow>
                  )),
                )}
            </TableBody>
          </Table>
          {hasNextPage && (
            <InViewBoxContainer>
              <Box ref={inViewRef} />
            </InViewBoxContainer>
          )}
        </TableBodyContainer>
      ) : (
        <NoResultAlert />
      )}
    </>
  );
};
